import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, RefreshCw, Search } from 'lucide-react'
import {
  listSecretFindings,
  rescanAppSecretFindings,
  type SecretScanFinding,
  updateSecretFindingStatus,
} from '../../lib/api'

const PAGE_SIZE = 50

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Unknown'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function severityBadge(severity: string) {
  const s = (severity || '').toLowerCase()
  if (s === 'high') return 'bg-red-500/20 text-red-500'
  if (s === 'medium') return 'bg-amber-500/20 text-amber-500'
  return 'bg-blue-500/20 text-blue-500'
}

function statusBadge(status: string) {
  const s = (status || '').toLowerCase()
  if (s === 'resolved') return 'bg-green-500/20 text-green-500'
  if (s === 'ignored') return 'bg-slate-500/20 text-slate-500'
  return 'bg-orange-500/20 text-orange-500'
}

export default function SecretFindings() {
  const [findings, setFindings] = useState<SecretScanFinding[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [severity, setSeverity] = useState<'high' | 'medium' | 'low' | ''>('')
  const [status, setStatus] = useState<'open' | 'resolved' | 'ignored' | ''>('open')
  const [appNameInput, setAppNameInput] = useState('')
  const [appNameFilter, setAppNameFilter] = useState('')
  const [page, setPage] = useState(0)
  const [updatingFindingId, setUpdatingFindingId] = useState<number | null>(null)
  const [rescanningAppName, setRescanningAppName] = useState<string | null>(null)

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

  const loadFindings = useCallback(async () => {
    setLoading(true)
    try {
      const data = await listSecretFindings({
        severity: severity || undefined,
        status: status || undefined,
        app_name: appNameFilter || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      setFindings(data.findings || [])
      setTotal(data.total || 0)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load secret findings')
    } finally {
      setLoading(false)
    }
  }, [severity, status, appNameFilter, page])

  useEffect(() => {
    loadFindings()
  }, [loadFindings])

  const handleUpdateStatus = useCallback(
    async (finding: SecretScanFinding, nextStatus: 'open' | 'resolved' | 'ignored') => {
      const withNote = nextStatus !== 'open'
      let note: string | undefined
      if (withNote) {
        const result = window.prompt(`Optional note for ${nextStatus}:`, finding.admin_note || '')
        if (result === null) return
        note = result.trim() || undefined
      }

      setUpdatingFindingId(finding.id)
      try {
        await updateSecretFindingStatus(finding.id, {
          status: nextStatus,
          note,
        })
        setNotice(`Updated finding #${finding.id} to ${nextStatus}.`)
        await loadFindings()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update finding status')
      } finally {
        setUpdatingFindingId(null)
      }
    },
    [loadFindings]
  )

  const handleRescanApp = useCallback(
    async (appName: string) => {
      setRescanningAppName(appName)
      try {
        const result = await rescanAppSecretFindings(appName)
        const summary = result.summary?.counts_by_severity || { high: 0, medium: 0, low: 0 }
        setNotice(
          `Rescan finished for ${appName}: total ${result.summary?.total_findings || 0}, high ${summary.high}, medium ${summary.medium}, low ${summary.low}.`
        )
        await loadFindings()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to rescan app')
      } finally {
        setRescanningAppName(null)
      }
    },
    [loadFindings]
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Secret Findings</h1>
          <p className="text-muted-foreground">Redacted secret scan issues across deployed apps</p>
        </div>
        <button
          onClick={loadFindings}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">{error}</div>
      )}
      {notice && (
        <div className="mb-4 p-4 rounded-lg bg-green-500/10 text-green-600 border border-green-500/20">
          {notice}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <select
          value={severity}
          onChange={(e) => {
            setSeverity(e.target.value as 'high' | 'medium' | 'low' | '')
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as 'open' | 'resolved' | 'ignored' | '')
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="ignored">Ignored</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={appNameInput}
            onChange={(e) => setAppNameInput(e.target.value)}
            placeholder="Filter by app name"
            className="pl-10 pr-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <button
          onClick={() => {
            setAppNameFilter(appNameInput.trim())
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
        >
          Apply
        </button>
        <button
          onClick={() => {
            setSeverity('')
            setStatus('open')
            setAppNameInput('')
            setAppNameFilter('')
            setPage(0)
          }}
          className="px-3 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
        >
          Clear
        </button>
        <div className="text-sm text-muted-foreground ml-auto">Showing {findings.length} of {total}</div>
      </div>

      {loading && findings.length === 0 ? (
        <div className="flex items-center justify-center h-56">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : findings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-border rounded-xl">No findings for current filters</div>
      ) : (
        <div className="border border-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[1300px]">
            <thead className="bg-secondary/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Severity</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium">App</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Rule</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Excerpt</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Hint</th>
                <th className="px-4 py-3 text-left text-sm font-medium">When</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {findings.map((finding) => {
                const rowBusy = updatingFindingId === finding.id || rescanningAppName === finding.app_name
                return (
                  <tr key={finding.id} className="hover:bg-secondary/20 align-top">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${severityBadge(finding.severity)}`}>
                        <AlertTriangle className="w-3 h-3" />
                        {(finding.severity || 'unknown').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className={`inline-flex items-center px-2 py-1 rounded font-medium ${statusBadge(finding.status || 'open')}`}>
                        {(finding.status || 'open').toUpperCase()}
                      </span>
                      {finding.admin_note && <div className="mt-2 text-muted-foreground break-words max-w-[220px]">{finding.admin_note}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">
                      <div>{finding.app_name || `#${finding.app_id}`}</div>
                      {finding.app_name && (
                        <button
                          onClick={() => handleRescanApp(finding.app_name)}
                          disabled={rowBusy}
                          className="mt-2 text-xs px-2 py-1 rounded border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          Rescan App
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{finding.rule_id || 'n/a'}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-mono break-all">{finding.file_path}</div>
                      <div className="text-muted-foreground">{finding.line_number ? `line ${finding.line_number}` : ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-muted px-2 py-1 rounded break-all">{finding.excerpt || '[no excerpt]'}</code>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[260px]">{finding.hint || '-'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatRelativeTime(finding.created_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-col gap-2 min-w-[120px]">
                        <button
                          onClick={() => handleUpdateStatus(finding, 'resolved')}
                          disabled={rowBusy || finding.status === 'resolved'}
                          className="px-2 py-1 rounded border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          Resolve
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(finding, 'ignored')}
                          disabled={rowBusy || finding.status === 'ignored'}
                          className="px-2 py-1 rounded border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          Ignore
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(finding, 'open')}
                          disabled={rowBusy || finding.status === 'open'}
                          className="px-2 py-1 rounded border border-border hover:bg-secondary transition-colors disabled:opacity-50"
                        >
                          Reopen
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
